import { createDropdown, Plugin } from 'ckeditor5';
import {
  attachTwoColumnPicker,
  collectionsFromGroups
} from './catalogueFieldPicker';

// Branch / fork glyph ("call_split") — reads as decision/condition logic.
// Filled, no explicit fill so it inherits the button colour via currentColor.
const icon = `
<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M14 4l2.29 2.29-2.88 2.88 1.42 1.42 2.88-2.88L20 12V4h-8zM10 4H4v8l2.29-2.29 4.71 4.7V20h2v-8.41l-5.29-5.3L10 4z"></path>
</svg>`;

/** Left-column categories: one per scalar group (collections are excluded). */
function buildCategories(groups) {
  const categories = [];
  groups.forEach((group) => {
    const scalars = (group.fields || []).filter((f) => f.kind !== 'collection');
    if (scalars.length) {
      categories.push({
        key: `g:${group.group}`,
        label: group.group,
        fields: scalars,
        isLoop: false
      });
    }
  });
  return categories;
}

const ifOnly = (cond) => `{{#if ${cond}}} {{/if}}`;
const ifElse = (cond) => `{{#if ${cond}}} {{else}} {{/if}}`;
const forEachInside = (cond, collectionToken) =>
  `{{#if ${cond}}} {{#each ${collectionToken}}} {{/each}} {{/if}}`;

function withForEachSection(
  t,
  title,
  classes,
  operatorNodes,
  collections,
  insert
) {
  const simple = operatorNodes.filter((n) => n.cond);
  if (!collections.length || !simple.length) {
    return { label: t, title, classes, children: operatorNodes };
  }
  return {
    label: t,
    title,
    classes,
    sections: [
      {
        key: 'field',
        label: 'Single value',
        expanded: true,
        nodes: operatorNodes
      },
      {
        key: 'foreach',
        label: 'Repeating values',
        expanded: false,
        nodes: simple.map((n) => ({
          label: n.label,
          title: `${t} ${n.label}`,
          children: collections.map((c) => ({
            label: c.token,
            onExecute: () => insert(forEachInside(n.cond, c.token))
          }))
        }))
      }
    ]
  };
}

function clinicalChain(t) {
  return (
    `{{#if ${t} == 'carrier'}} {{else}}` +
    `{{#if ${t} == 'non_carrier'}} {{else}}` +
    `{{#if ${t} == 'unknown'}} {{/if}}{{/if}}{{/if}}`
  );
}

function buildConditionNode(field, insert, collections) {
  const t = field.token;
  const op = (label, scaffold, cond) => ({
    label,
    onExecute: () => insert(scaffold),
    cond
  });

  const classes =
    field.kind === 'clinical_assertion' ? ['ck-merge-field-clinical'] : [];
  const wrap = (children) =>
    withForEachSection(t, t, classes, children, collections, insert);

  if (field.kind === 'collection') {
    return {
      label: t,
      title: t,
      classes,
      children: [
        op('has at least one', ifOnly(`${t} is not empty`)),
        op('has at least one, otherwise…', ifElse(`${t} is not empty`)),
        op('is empty (none recorded)', ifOnly(`${t} is empty`))
      ]
    };
  }

  if (field.kind === 'clinical_assertion') {
    return wrap([
      op('Carrier / not carrier / unknown — full branch', clinicalChain(t)),
      op('is a carrier', ifOnly(`${t} == 'carrier'`), `${t} == 'carrier'`),
      op(
        'is not a carrier',
        ifOnly(`${t} == 'non_carrier'`),
        `${t} == 'non_carrier'`
      ),
      op(
        'is unknown / not established',
        ifOnly(`${t} == 'unknown'`),
        `${t} == 'unknown'`
      )
    ]);
  }

  if (field.kind === 'date') {
    return wrap([
      op('has a value', ifOnly(`${t} is not empty`), `${t} is not empty`),
      op('has a value, otherwise…', ifElse(`${t} is not empty`)),
      op('is blank', ifOnly(`${t} is empty`), `${t} is empty`)
    ]);
  }

  if (field.boolean) {
    return wrap([
      op('is Yes / checked', ifOnly(`${t}`), `${t}`),
      op('is Yes, otherwise…', ifElse(`${t}`)),
      op('is No / unchecked', ifOnly(`${t} == 'No'`), `${t} == 'No'`),
      op('has a value', ifOnly(`${t} is not empty`), `${t} is not empty`),
      op('is blank', ifOnly(`${t} is empty`), `${t} is empty`)
    ]);
  }

  // Fields with a known set of values (e.g. Sex) get one-click value choices.
  const valueBranch = (opSym) => ({
    label: opSym === '==' ? 'equals…' : 'does not equal…',
    title: `${t} ${opSym === '==' ? 'equals' : 'does not equal'}`,
    children: [
      ...field.enumValues.map((v) => op(v, ifOnly(`${t} ${opSym} '${v}'`))),
      op('a custom value…', ifOnly(`${t} ${opSym} 'value'`))
    ]
  });

  const children = [
    op('has a value', ifOnly(`${t} is not empty`), `${t} is not empty`),
    op('has a value, otherwise…', ifElse(`${t} is not empty`)),
    op('is blank', ifOnly(`${t} is empty`), `${t} is empty`)
  ];
  if (Array.isArray(field.enumValues) && field.enumValues.length) {
    children.push(valueBranch('=='), valueBranch('!='));
  } else {
    children.push(
      op('equals a value…', ifOnly(`${t} == 'value'`), `${t} == 'value'`),
      op(
        'does not equal a value…',
        ifOnly(`${t} != 'value'`),
        `${t} != 'value'`
      )
    );
  }

  if (field.numeric) {
    children.push({
      label: 'compare as number…',
      title: `${t} — number`,
      children: [
        op('is greater than…', ifOnly(`${t} > '0'`)),
        op('is at least (≥)…', ifOnly(`${t} >= '0'`)),
        op('is less than…', ifOnly(`${t} < '0'`)),
        op('is at most (≤)…', ifOnly(`${t} <= '0'`))
      ]
    });
  } else {
    children.push(
      op('contains…', ifOnly(`${t} contains 'text'`), `${t} contains 'text'`),
      op(
        'does not contain…',
        ifOnly(`${t} does not contain 'text'`),
        `${t} does not contain 'text'`
      ),
      op(
        'starts with…',
        ifOnly(`${t} starts with 'text'`),
        `${t} starts with 'text'`
      )
    );
  }
  return wrap(children);
}

export default class InsertConditionalBlock extends Plugin {
  static get pluginName() {
    return 'InsertConditionalBlock';
  }

  init() {
    const editor = this.editor;

    editor.ui.componentFactory.add('insertConditionalBlock', (locale) => {
      const dropdownView = createDropdown(locale);
      dropdownView.buttonView.set({
        label: 'Condition',
        tooltip: 'Insert an IF condition (include content only when…)',
        icon,
        withText: true
      });

      dropdownView.extendTemplate({
        attributes: {
          class: ['ck-insert-conditional-dropdown', 'tgc-tb-logic'],
          'data-va-anchor': 'pedigree.contact.conditionDropdown'
        }
      });

      const groups = editor.config.get('mergeFieldsCatalogue') || [];
      const categories = buildCategories(groups);
      const collections = collectionsFromGroups(groups);

      const insertScaffold = (scaffold) => {
        editor.model.change((writer) => {
          writer.insertText(
            scaffold,
            editor.model.document.selection.getFirstPosition()
          );
        });
        dropdownView.isOpen = false;
      };

      attachTwoColumnPicker({
        locale,
        editor,
        dropdownView,
        categories,
        searchPlaceholder: 'Search all fields…',
        describeField: (field) =>
          buildConditionNode(field, insertScaffold, collections)
      });

      return dropdownView;
    });
  }
}
